import { useTranslation } from "react-i18next";
import { cn } from "../utils/misc";

interface SkeletonProps {
    className?: string;
    variant?: 'text' | 'circular' | 'rectangular';
    width?: string | number;
    height?: string | number;
    animation?: 'pulse' | 'wave' | 'none';
}

export function Skeleton({
    className,
    variant = 'rectangular',
    width,
    height,
    animation = 'pulse'
}: SkeletonProps) {
    const baseClasses = "bg-gray-200 dark:bg-gray-700";

    const variantClasses = {
        text: "rounded h-4",
        circular: "rounded-full",
        rectangular: "rounded"
    };

    const animationClasses = {
        pulse: "animate-pulse motion-reduce:animate-none",
        wave: "animate-shimmer motion-reduce:animate-none bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700",
        none: ""
    };

    const style: React.CSSProperties = {};
    if (width) style.width = typeof width === 'number' ? `${width}px` : width;
    if (height) style.height = typeof height === 'number' ? `${height}px` : height;

    return (
        <div
            aria-hidden="true"
            className={cn(
                baseClasses,
                variantClasses[variant],
                animationClasses[animation],
                className
            )}
            style={style}
        />
    );
}

/**
 * Wraps a skeleton composition with a single live region for the zone it represents.
 * Use once per loading zone (a whole page, a panel, a table) — never per individual
 * skeleton block, or screen reader users get one announcement per pulsing rectangle.
 */
export function LoadingStatus({ label, className, children }: {
    label?: string;
    className?: string;
    children?: React.ReactNode;
}) {
    const { t } = useTranslation();
    return (
        <div role="status" aria-live="polite" aria-busy="true" className={className}>
            <span className="sr-only">{label ?? t('loading')}</span>
            {children}
        </div>
    );
}

// Preset skeleton components for common use cases

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
    return (
        <div className={cn("space-y-2", className)}>
            {Array.from({ length: lines }).map((_, i) => (
                <Skeleton
                    key={i}
                    variant="text"
                    width={i === lines - 1 ? "80%" : "100%"}
                />
            ))}
        </div>
    );
}

export function SkeletonCard({ className }: { className?: string }) {
    return (
        <div className={cn("panel", className)}>
            <div className="flex items-center gap-4 mb-4">
                <Skeleton variant="circular" width={48} height={48} />
                <div className="flex-1 space-y-2">
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                </div>
            </div>
            <SkeletonText lines={3} />
        </div>
    );
}

export function SkeletonTable({ rows = 5, columns = 4, className }: {
    rows?: number;
    columns?: number;
    className?: string;
}) {
    return (
        <div className={cn("w-full space-y-3", className)}>
            <div className="flex gap-4">
                {Array.from({ length: columns }).map((_, i) => (
                    <Skeleton key={i} variant="text" height={20} className="flex-1" />
                ))}
            </div>
            {Array.from({ length: rows }).map((_, r) => (
                <div key={r} className="flex gap-4">
                    {Array.from({ length: columns }).map((_, c) => (
                        <Skeleton key={c} variant="text" height={16} className="flex-1" />
                    ))}
                </div>
            ))}
        </div>
    );
}

export function SkeletonChart({ className, height = 300 }: { className?: string; height?: number }) {
    return (
        <div className={cn("panel", className)}>
            <Skeleton variant="text" width="40%" className="mb-4" />
            <Skeleton variant="rectangular" height={height} className="w-full" />
        </div>
    );
}

export function SkeletonStat({ className }: { className?: string }) {
    return (
        <div className={cn("panel", className)}>
            <div className="flex justify-between items-start">
                <div className="flex-1 space-y-2">
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="40%" />
                </div>
                <Skeleton variant="circular" width={40} height={40} />
            </div>
        </div>
    );
}
